/**
 * Moteur d'anonymisation du mode démo.
 *
 * Module PUR (dépend uniquement de ./dictionaries) — copié tel quel dans la
 * codebase mobile.
 *
 * Principe : substitution DÉTERMINISTE par nom de champ. La dénormalisation
 * massive du modèle (animalName, enclosureName, userName… recopiés partout)
 * fait que le registre par champ couvre toute vue actuelle et future sans
 * connaître les collections. Le hash porte sur la VALEUR réelle normalisée :
 * le même nom réel donne le même nom fictif partout (toutes vues, toutes
 * sessions, web et mobile).
 *
 * Textes saisis par les parcs (décision utilisateur 2026-09-24, qui remplace
 * celle du 2026-07-05) : titres et descriptions de tâches, notes, informations,
 * commentaires, noms d'enrichissements et de campagnes, textes de rapports
 * sont remplacés par des textes fictifs de demoTexts.js, dans la langue de
 * l'interface (une démo en anglais est entièrement en anglais). Les clés
 * génériques (title, name, description, content…) ne sont touchées que sur
 * les documents dont le type est reconnu (ENTITY_TEXT_RULES) : les documents
 * de configuration (grilles, questions, paramètres, étiquettes, modèles)
 * gardent leurs libellés.
 *
 * Ce qui n'est PAS touché : valeurs, stats, dates, unités, espèces,
 * comportements, paramètres, médicaments, contenus AKONGO (catalogue
 * officiel, guides partagés).
 */

import {
  ANIMAL_NAMES,
  FIRST_NAMES,
  LAST_NAMES,
  ENCLOSURE_NAMES,
  SECTOR_NAMES,
  GROUP_NAMES,
  ZOO_NAMES,
  CLINIC_NAMES,
  TOURNEE_NAMES,
  ADDRESSES,
} from "./dictionaries";
import {
  TASK_TITLES,
  TASK_DESCRIPTIONS,
  TASK_CATEGORY_KEYWORDS,
  NOTE_TEXTS,
  NOTE_TITLES,
  INFORMATION_TITLES,
  COMMENT_TEXTS,
  DIAGNOSIS_TEXTS,
  TREATMENT_INSTRUCTIONS,
  GROUP_OBJECTIVES,
  GROUP_ACTIONS,
  TRAINING_EXERCISES,
  REINFORCERS,
  DOCUMENT_TITLES,
  ENRICHMENT_NAMES,
  CAMPAIGN_NAMES,
  STANDARD_BEHAVIORS,
  BEHAVIOR_HELP_TEXTS,
  ANSWER_GLOSSARY,
  CONTEXT_QUESTIONS_BY_ANSWER_TYPE,
  CONTEXT_TEMPLATES,
  CONTEXT_OPEN_QUESTIONS,
  QUESTIONNAIRE_NAMES,
  ETHOGRAM_NAMES,
} from "./demoTexts";
import { isDemoPhotosActive } from "./demoState";
import { normalizeSearchTerm } from "../utils/search/searchMatch";

// --- Locale ---------------------------------------------------------------

let localeProvider = () => "en";

/** Le wiring web branche i18n.language ici ; le mobile branche sa propre source. */
export function setLocaleProvider(provider) {
  localeProvider = provider;
}

function currentLang() {
  const raw = String(localeProvider() || "en").toLowerCase();
  const lang = raw.split("-")[0];
  return ["fr", "en", "de"].includes(lang) ? lang : "en";
}

// --- Hash / picking déterministes ------------------------------------------

/** FNV-1a 32 bits sur la valeur normalisée (trim + lowercase). */
export function hashString(input) {
  const str = String(input).trim().toLowerCase();
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function pick(list, seed) {
  return list[hashString(seed) % list.length];
}

function pickLocalized(list, seed) {
  const entry = pick(list, seed);
  return entry[currentLang()] || entry.en;
}

function pickLocalizedIn(list, seed, lang) {
  const entry = pick(list, seed);
  return entry[lang] || entry.en;
}

// --- Noms uniques (structure du parc) -------------------------------------------
// Deux vrais secteurs ne doivent pas porter le même nom fictif : le tirage par
// hash donne la place de départ, et si elle est déjà prise par un autre nom
// réel, on prend la suivante libre. Quand le dictionnaire est épuisé, les
// noms repartent avec un numéro (« Secteur Asie 2 »). Les attributions sont
// identifiées par l'empreinte du nom réel (jamais le nom lui-même) et peuvent
// être sauvegardées sur l'appareil pour rester stables d'une session à l'autre.
const UNIQUE_NAME_KINDS = new Set(["enclosure", "sector", "group", "tournee", "zoo"]);
const nameSlots = new Map(); // kind → { byReal: Map(empreinte → [index, tour]), used: Set("index:tour") }
let nameSlotsListener = null;

function slotsFor(kind) {
  let slots = nameSlots.get(kind);
  if (!slots) {
    slots = { byReal: new Map(), used: new Set() };
    nameSlots.set(kind, slots);
  }
  return slots;
}

function uniqueSlot(kind, size, value) {
  const slots = slotsFor(kind);
  const fingerprint = hashString(value).toString(36);
  const known = slots.byReal.get(fingerprint);
  if (known) return known;
  const start = hashString(value) % size;
  for (let round = 0; ; round++) {
    for (let step = 0; step < size; step++) {
      const index = (start + step) % size;
      if (!slots.used.has(`${index}:${round}`)) {
        const slot = [index, round];
        slots.used.add(`${index}:${round}`);
        slots.byReal.set(fingerprint, slot);
        if (nameSlotsListener) nameSlotsListener();
        return slot;
      }
    }
  }
}

function pickUniqueLocalized(kind, list, value) {
  const [index, round] = uniqueSlot(kind, list.length, value);
  const entry = list[index];
  const name = entry[currentLang()] || entry.en;
  return round ? `${name} ${round + 1}` : name;
}

/** Attributions de noms uniques, à sauvegarder sur l'appareil. */
export function exportNameSlots() {
  const data = {};
  for (const [kind, slots] of nameSlots) data[kind] = [...slots.byReal.entries()];
  return data;
}

/** Restaure des attributions sauvegardées (avant la première lecture). */
export function importNameSlots(data) {
  if (!data || typeof data !== "object") return;
  for (const [kind, entries] of Object.entries(data)) {
    if (!UNIQUE_NAME_KINDS.has(kind) || !Array.isArray(entries)) continue;
    const slots = slotsFor(kind);
    for (const [fingerprint, slot] of entries) {
      if (!Array.isArray(slot) || slots.byReal.has(fingerprint) || slots.used.has(`${slot[0]}:${slot[1]}`)) continue;
      slots.byReal.set(fingerprint, slot);
      slots.used.add(`${slot[0]}:${slot[1]}`);
    }
  }
}

/** Oublie toutes les attributions (copies de démo jetées). */
export function resetNameSlots() {
  nameSlots.clear();
}

/** Appelé à chaque nouvelle attribution (sauvegarde par le câblage). */
export function setNameSlotsListener(listener) {
  nameSlotsListener = listener;
}

// --- Registre des champs sensibles -----------------------------------------

// kind → fonction de substitution ; FIELD_KINDS mappe les noms de champs
// Firestore (y compris toutes les copies dénormalisées) vers un kind.
const FIELD_KINDS = {
  // Animaux — destinationName (notes/notifications) porte le plus souvent un
  // nom d'animal ou d'enclos ; on privilégie l'absence de fuite au réalisme.
  animalName: "animal",
  destinationName: "animal",

  // Copies dénormalisées sur les fiches animal (index mobile, cache SQLite)
  animalEnclosureName: "enclosure",
  animalGroupName: "group",

  // Structure du parc
  enclosureName: "enclosure",
  sourceEnclosureName: "enclosure",
  targetEnclosureName: "enclosure",
  areaName: "enclosure",
  // ZIMS collection : enclos/section parent
  parentName: "enclosure",
  sectorName: "sector",
  // Copie du secteur de rattachement sur la fiche enclos
  linkedSectorName: "sector",
  // Alias dénormalisé de secteur/zone (enrichissements, analyses d'espace)
  zoneName: "sector",
  groupName: "group",
  groupTitle: "group",
  zooName: "zoo",
  zooManagerName: "fullName",
  // Guides d'enrichissement partagés entre parcs (auteur, commentaires)
  ownerZooName: "zoo",
  authorZooName: "zoo",
  tourneeName: "tournee",
  // Rapports quotidiens : copie du nom de la tournée (ou du secteur) d'origine
  sourceName: "tournee",

  // Personnes — noms complets dénormalisés
  userName: "fullName",
  authorName: "fullName",
  createdByName: "fullName",
  updatedByName: "fullName",
  evaluatorName: "fullName",
  observerName: "fullName",
  contributorName: "fullName",
  recordedByName: "fullName",
  veterinaryName: "fullName",
  vetName: "fullName",
  officialName: "fullName",
  modifiedByName: "fullName",
  keeperName: "fullName",
  displayName: "fullName",
  // Rapports de visite / historique d'édition de campagne
  validatedByName: "fullName",
  editedByName: "fullName",
  // Variantes mobiles (chat d'équipe, assignations d'actions)
  responderName: "fullName",
  teamResponderName: "fullName",
  assignedToName: "fullName",
  // Liste des tâches de campagne (cache mobile), ordonnances, notes
  // supprimées, séances de training
  lastUserName: "fullName",
  veterinarianName: "fullName",
  deletedByName: "fullName",
  operatorName: "fullName",

  // Personnes — prénom / nom séparés (les deux casses coexistent dans le code)
  userFirstName: "firstName",
  userLastName: "lastName",
  userFirstname: "firstName",
  obsUserFirstname: "firstName",
  quizzUserFirstname: "firstName",
  firstName: "firstName",
  firstname: "firstName",
  userLastname: "lastName",
  obsUserLastname: "lastName",
  quizzUserLastname: "lastName",
  lastName: "lastName",
  lastname: "lastName",

  // Coordonnées / identifiants
  userMail: "email",
  userEmail: "email",
  email: "email",
  phone: "phone",
  clinicPhone: "phone",
  phoneNumber: "phone",
  clinicName: "clinic",
  gan: "identifier",
  animalGan: "identifier",
  animalLocalId: "identifier",
  localId: "identifier",
  chip: "identifier",
  licenseNumber: "identifier",
  houseName: "identifier",
  animalHouseName: "identifier",

  // Fiche établissement (paramétrage du parc). zooId et nemo restent intacts :
  // ce sont des clés techniques (documents, ZIMS) — masqués à l'affichage.
  zooEmail: "email",
  zooPhone: "phone",
  zooAddress: "address",
  responsibleName: "fullName",
  zooBackupName: "fullName",
  // Connexion ZIMS du parc (zimsSyncConfig)
  zimsLogin: "email",
  // Ordonnances : vétérinaire saisi en texte libre
  veterinarian: "fullName",
  // Nom d'un éthogramme du parc (souvent « Espèce_Nom du parc »)
  obsName: "ethogramName",

  // Noms de toute nature recopiés sans leur type (cibles d'une tâche, entités
  // d'une réponse de rapport) : même nom fictif que là où le type est connu
  targetNames: "knownName",
  entityName: "knownName",
  contextName: "knownName",
  // Journaux : zone de l'enclos ; notifications et interventions : personnes
  currentAreaName: "enclosure",
  noteAuthorName: "fullName",
  reportedByName: "fullName",
  qualifiedByName: "fullName",
  completedByName: "fullName",
  verifiedByName: "fullName",

  // Textes saisis par les parcs (clés sans ambiguïté)
  campaignName: "campaign",
  enrichmentName: "enrichment",
  enrichName: "enrichment",
  objectName: "enrichment",
  userFeedback: "note",
  userMessage: "note",
  teamMessage: "comment",
  // Réponses d'équipe multiples {message, responderName…} (ANA-39)
  teamReplies: "comment",
  akongoMessage: "comment",
  comment: "comment",
  comments: "comment",
  entityComments: "comment",
  nextTime: "comment",
  // Séance de training (fiche v12) : précision sur l'état de santé
  healthNote: "comment",
  chatResponse: "comment",
  animalNotes: "note",
  campaignObjectiveText: "note",
  campaignObjective: "html",
  projectName: "campaign",
  diagnosis: "diagnosis",
  reinforcer: "reinforcer",
  rafikiSummary: "html",
};

// Textes fictifs : un kind de texte remplace aussi les textes contenus dans
// des objets {text, comment, message, content} ou des maps {id: texte}.
const TEXT_KINDS = new Set([
  "campaign", "enrichment", "note", "answer", "noteTitle", "comment", "informationTitle",
  "taskTitle", "taskDescription", "diagnosis", "instruction", "objective",
  "action", "exercise", "reinforcer", "document", "html", "bullets",
]);
const TEXT_CONTAINER_PROPS = ["text", "comment", "message", "content"];

// Champs dont la valeur est une option react-select {label, value} où le
// label porte un nom sensible mais dont la clé n'est pas dans FIELD_KINDS.
const OPTION_LABEL_KINDS = {
  animalEnclosureData: "enclosure",
  userZoo: "zoo",
  // Éthogramme choisi pour une campagne de scan (le nom porte souvent le parc)
  scanEthogramme: "ethogramName",
};

// --- Photos factices (sous-option du mode démo) ------------------------------

// Champs image connus par entité (cf. audit photoUploadService / connecteurs).
const PHOTO_FIELDS = new Set([
  "animalProfilePicture",
  "enclosureProfilePicture",
  "sectorProfilePicture",
  "profilePicture",
  "profilePictureUrl",
  "photo",
  "photos",
  "photoURL",
  "groupPhoto",
  "thumbnail",
  "logo",
  "media",
  "mediaList",
  // Champs mobiles (sessions, évaluations, feed)
  "sessionPhoto",
  "evaluationPhoto",
  "imageUrl",
  // Avatar des personnes, ancien champ photo des animaux, photo d'un objet
  "userProfilePicture",
  "animalPictureProfil",
  "mediaLocation",
  // Photo d'un groupe FAST
  "groupProfilePicture",
  // Vignette Mux d'un document `videos` (vidéos des fiches d'exercice training)
  "mux_thumbnail_url",
]);

// Photos de profil : en mode démo on INJECTE une photo factice même quand
// l'entité n'en a pas (une démo avec des fiches sans photo est triste).
const PROFILE_PHOTO_FIELDS = new Set([
  "animalProfilePicture",
  "enclosureProfilePicture",
  "sectorProfilePicture",
  "groupPhoto",
]);

// Références Storage : neutralisées après substitution, sinon les vues qui
// résolvent l'image via bddRef (imageController) réafficheraient la vraie photo.
const PHOTO_REF_PROPS = new Set([
  "bddRef",
  "thumbBddRef",
  "storagePath",
  "public_id",
  // Galerie des évaluations rapides (mobile)
  "storageRef",
  "thumbnailRef",
]);

// Propriétés portant une URL d'image dans les objets photo {url, uri, thumbUrl, bddRef…}
const PHOTO_URL_PROPS = new Set([
  "url",
  "uri",
  "thumbUrl",
  "thumb",
  "thumbnailUrl",
  "downloadURL",
]);

// Injecté par le câblage (web : placeholder bundlé aujourd'hui, banque de
// photos IA en phase 2 ; mobile : sa propre source). Reçoit l'URL réelle en
// seed pour permettre un picking déterministe.
let photoResolver = null;

export function setDemoPhotoResolver(resolver) {
  photoResolver = resolver;
}

// Document joint (PDF d'une fiche d'exercice training) : pas une image, son
// lien reste celui du document au lieu d'ouvrir une photo factice.
function isDocumentMedia(value) {
  return (
    isPlainObject(value) &&
    (value.type === "document" ||
      (typeof value.mimeType === "string" && value.mimeType === "application/pdf"))
  );
}

function isVideoMedia(value) {
  return (
    isPlainObject(value) &&
    (value.type === "video" ||
      (typeof value.mimeType === "string" && value.mimeType.startsWith("video")))
  );
}

function substitutePhoto(value, fieldKey, nodeCtx) {
  if (!photoResolver) return value;
  const nodeSeed = nodeCtx ? nodeCtx.seed : null;
  // Injection : entité sans photo de profil → photo factice quand même
  if (!value && PROFILE_PHOTO_FIELDS.has(fieldKey) && nodeSeed) {
    const fake = photoResolver(nodeSeed, fieldKey, nodeCtx);
    return { uri: fake, url: fake, thumbUrl: fake, bddRef: null };
  }
  if (typeof value === "string") {
    return value.trim() ? photoResolver(value, fieldKey, nodeCtx) : value;
  }
  if (Array.isArray(value)) return value.map((item) => substitutePhoto(item, fieldKey, nodeCtx));
  if (isDocumentMedia(value)) return transform(value);
  if (isVideoMedia(value)) {
    // Vidéo : le player reste intact (une image factice le casserait) mais la
    // VIGNETTE est substituée — c'est elle qui s'affiche dans les feeds
    const thumbSeed =
      value.mux_thumbnail_url || value.thumbUrl || value.thumbnailUrl || value.url ||
      (nodeCtx ? nodeCtx.seed : null);
    if (!thumbSeed) return value;
    const fakeThumb = photoResolver(thumbSeed, fieldKey, nodeCtx);
    const out = { ...value };
    for (const k of ["thumbUrl", "thumbnailUrl", "mux_thumbnail_url", "videoThumbnail", "thumb"]) {
      if (typeof out[k] === "string" && out[k]) out[k] = fakeThumb;
    }
    if (typeof out.thumbBddRef === "string") out.thumbBddRef = null;
    return out;
  }
  if (isPlainObject(value)) {
    // Seed unique par objet photo : vignette et image pleine résolution
    // pointent vers la même photo factice
    const seed =
      [...PHOTO_URL_PROPS]
        .map((k) => value[k])
        .find((v) => typeof v === "string" && v.trim()) ||
      [...PHOTO_REF_PROPS]
        .map((k) => value[k])
        .find((v) => typeof v === "string" && v.trim()) ||
      nodeSeed;
    if (!seed) return value;
    const fake = photoResolver(seed, fieldKey, nodeCtx);
    const out = {};
    for (const [key, v] of Object.entries(value)) {
      if (PHOTO_URL_PROPS.has(key) && typeof v === "string" && v.trim()) out[key] = fake;
      else if (PHOTO_REF_PROPS.has(key)) out[key] = null;
      else out[key] = transform(v);
    }
    // Garantit qu'au moins url/uri portent la photo factice, même si l'objet
    // n'avait qu'une référence Storage
    if (!out.url) out.url = fake;
    if (!out.uri) out.uri = fake;
    return out;
  }
  return value;
}

// --- Substitution par kind --------------------------------------------------

function stripAccents(str) {
  return str.normalize("NFD").replace(/[̀-ͯ]/g, "");
}


function fakeEmail(firstName, lastName) {
  const slug = (s) => stripAccents(String(s)).toLowerCase().replace(/[^a-z]/g, "");
  return `${slug(firstName)}.${slug(lastName)}@demo.akongo.app`;
}

function fakeFullName(value) {
  const tokens = String(value).trim().split(/\s+/);
  const first = pick(FIRST_NAMES, tokens[0]);
  const lastSeed = tokens.length > 1 ? tokens.slice(1).join(" ") : tokens[0];
  const last = pick(LAST_NAMES, lastSeed);
  return `${first} ${last}`;
}

function fakePhone(value) {
  const h = hashString(value);
  const d = (n) => String((h >> n) % 90 + 10).slice(0, 2);
  return `+33 3 ${d(0)} ${d(4)} ${d(8)} ${d(12)}`;
}

// Index inverse nom réel → nom(s) fictif(s), alimenté au fil des substitutions.
// Permet la recherche par nom ORIGINAL en mode démo : le démonstrateur tape le
// vrai nom (jamais affiché) et matche l'entité rendue sous son nom fictif.
const reverseSearchIndex = new Map(); // realNormalisé -> Set<fakeNormalisé>

function registerSubstitution(realValue, fakeValue) {
  if (typeof realValue !== "string" || typeof fakeValue !== "string") return;
  const real = normalizeSearchTerm(realValue);
  if (!real) return;
  let fakes = reverseSearchIndex.get(real);
  if (!fakes) {
    fakes = new Set();
    reverseSearchIndex.set(real, fakes);
  }
  fakes.add(normalizeSearchTerm(fakeValue));
}

/**
 * Filtre de recherche compatible mode démo.
 * Matche si la requête est contenue dans un des textes AFFICHÉS (comportement
 * standard), OU si elle correspond à un nom RÉEL dont le substitut fictif
 * apparaît dans les textes affichés. Le nom réel n'est jamais retourné.
 *
 * La comparaison passe par normalizeSearchTerm : « boite » trouve « Boîte »
 * (GitLab #1075), des deux côtés de l'index de substitution.
 */
export function demoSearchMatch(query, ...displayedTexts) {
  const q = normalizeSearchTerm(query);
  if (!q) return true;
  const haystack = displayedTexts
    .filter((t) => t !== null && t !== undefined)
    .map(normalizeSearchTerm);
  if (haystack.some((h) => h.includes(q))) return true;
  for (const [real, fakes] of reverseSearchIndex) {
    if (real.includes(q)) {
      for (const fake of fakes) {
        if (haystack.some((h) => h.includes(fake))) return true;
      }
    }
  }
  return false;
}

export function substitute(kind, value, extra) {
  const result = substituteByKind(kind, value, extra);
  registerSubstitution(value, result);
  rememberValue(value, kind, extra);
  return result;
}

// Valeur réelle exacte → kind sous lequel elle a été substituée. Sert aux
// copies recopiées sans leur type (targetNames, entityName, titre de tâche
// repris par ses notes) : même texte fictif que l'original. On garde le kind,
// pas le texte fictif, pour suivre la langue courante.
const knownValues = new Map();

function rememberValue(realValue, kind, extra) {
  if (kind === "knownName" || typeof realValue !== "string" || !realValue.trim()) return;
  knownValues.set(realValue.trim(), { kind, extra });
}

// --- Textes fictifs -------------------------------------------------------------

// Catégorie d'une tâche : type structuré (taskType), mots-clés du titre, puis
// famille du type ou ancienne catégorie.
const TASK_VALUE_CATEGORY = {
  prep_rations: "feeding", distribute: "feeding", fill_water: "feeding",
  clean_bowls: "cleaning", check_automatic_systems: "maintenance",
  clean_enclosure: "cleaning", remove_waste: "cleaning", disinfect_equipment: "cleaning",
  check_fences: "maintenance", manage_substrate: "maintenance",
  observe_condition: "observation", count_animals: "observation", weighing: "weighing",
  check_coat: "health", check_injuries: "health", administer_treatment: "health",
  prepare_vet_visit: "health", assist_vet: "health", health_check: "health",
  collect_sample: "health", restock_supplies: "other", update_registry: "other",
  report_issues: "maintenance", check_before_closing: "other", install_barriers: "maintenance",
  isolate_animal: "health", handle_escape: "other",
};
const TASK_TYPE_CATEGORY = {
  feeding: "feeding", maintenance: "cleaning", animal_check: "observation",
  medical: "health", logistics: "other", safety: "maintenance",
  FEEDING: "feeding", MAINTENANCE: "maintenance", CLEANING: "cleaning",
  MEDICAL_PROCEDURE: "health", CHECK: "observation", VALIDATION: "other",
};

function categoryFromText(text) {
  const normalized = stripAccents(String(text || "")).toLowerCase();
  if (!normalized) return null;
  for (const [category, words] of Object.entries(TASK_CATEGORY_KEYWORDS)) {
    if (words.some((word) => normalized.includes(word))) return category;
  }
  return null;
}

function taskCategoryOf(node) {
  const taskType = node.taskType;
  const typeValue = typeof taskType === "string" ? taskType : taskType && taskType.value;
  const typeFamily = taskType && typeof taskType === "object" ? taskType.type : null;
  return (
    TASK_VALUE_CATEGORY[typeValue] ||
    categoryFromText(node.title || node.taskName) ||
    TASK_TYPE_CATEGORY[typeFamily] ||
    TASK_TYPE_CATEGORY[node.category] ||
    TASK_TYPE_CATEGORY[node.type] ||
    null
  );
}

function fakeTaskTitle(value, category) {
  const resolved = category || categoryFromText(value) || "other";
  return pickLocalized(TASK_TITLES[resolved] || TASK_TITLES.other, value);
}

// Tâche d'ordonnance générée par le web : « [Groupe: <nom>] <médicament> ».
// Le médicament est gardé, le nom du groupe ou de l'individu est remplacé.
const TREATMENT_TARGET_LABELS = {
  group: { fr: "Groupe", en: "Group", de: "Gruppe" },
  animal: { fr: "Individu", en: "Animal", de: "Tier" },
};

function fakeTreatmentTitle(value) {
  const match = /^\[([^:\]]+):\s*([^\]]+)\]\s*(.*)$/.exec(value);
  if (!match) return value;
  const isGroup = /group|groupe|gruppe/i.test(match[1]);
  const label = TREATMENT_TARGET_LABELS[isGroup ? "group" : "animal"];
  const name = substitute(isGroup ? "group" : "animal", match[2].trim());
  return `[${label[currentLang()] || label.en}: ${name}] ${match[3]}`;
}

// Nom d'enrichissement installé : les copies portent un suffixe « - 2 ».
function fakeEnrichmentName(value) {
  const match = /^(.*?)(\s-\s\d+)$/.exec(value);
  if (match && match[1].trim()) return pickLocalized(ENRICHMENT_NAMES, match[1]) + match[2];
  return pickLocalized(ENRICHMENT_NAMES, value);
}

function escapeHtml(text) {
  return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Note fictive : une phrase, deux pour un texte long ou pour une réponse de
// rapport. Les vues comparent parfois les textes (dédoublonnage des notes,
// accord entre soigneurs d'un rapport collaboratif) : deux phrases donnent
// des centaines de combinaisons et évitent les fausses égalités.
// Brassage final (murmur3 fmix32) : FNV-1a répartit mal les textes voisins
// (« Réponse 1 », « Réponse 2 ») sur un petit modulo. Réservé aux notes :
// hashString reste inchangé pour garder les tirages identiques au web.
function mixHash(input) {
  let h = hashString(input);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

function fakeNoteIn(value, lang, twoSentences) {
  const list = NOTE_TEXTS;
  const first = mixHash(value) % list.length;
  const firstText = list[first][lang] || list[first].en;
  if (!twoSentences && String(value).trim().length <= 60) return firstText;
  let second = mixHash(`${value}#2`) % list.length;
  if (second === first) second = (second + 1) % list.length;
  return `${firstText} ${list[second][lang] || list[second].en}`;
}

function plainTextOf(html) {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

// Texte à puces du rapport Rafiki vétérinaire : la pastille de priorité
// (🔴🟠🟢) et la puce sont gardées, le texte de chaque ligne est remplacé.
function fakeBulletText(value) {
  return String(value)
    .split("\n")
    .map((line) => {
      if (!line.trim()) return line;
      const match = /^(\s*(?:[-*•]\s*)?(?:\u{1F534}|\u{1F7E0}|\u{1F7E2})?\s*)(.*)$/u.exec(line);
      const prefix = match ? match[1] : "";
      const text = match ? match[2] : line;
      return text.trim() ? `${prefix}${fakeNoteIn(text, currentLang(), false)}` : line;
    })
    .join("\n");
}

function substituteByKind(kind, value, extra) {
  switch (kind) {
    case "animal":
      return pick(ANIMAL_NAMES, value);
    case "enclosure":
      return pickUniqueLocalized("enclosure", ENCLOSURE_NAMES, value);
    case "sector":
      return pickUniqueLocalized("sector", SECTOR_NAMES, value);
    case "group":
      return pickUniqueLocalized("group", GROUP_NAMES, value);
    case "zoo":
      return pickUniqueLocalized("zoo", ZOO_NAMES, value);
    case "clinic":
      return pickLocalized(CLINIC_NAMES, value);
    case "tournee":
      return pickUniqueLocalized("tournee", TOURNEE_NAMES, value);
    case "fullName":
      return fakeFullName(value);
    case "firstName":
      return pick(FIRST_NAMES, value);
    case "lastName":
      return pick(LAST_NAMES, value);
    case "email":
      return fakeEmail(pick(FIRST_NAMES, value), pick(LAST_NAMES, value));
    case "phone":
      return fakePhone(value);
    case "identifier":
      return `DEMO-${(hashString(value) % 900000) + 100000}`;
    case "address":
      return pickLocalized(ADDRESSES, value);
    case "domain":
      return `demo-${(hashString(value) % 9000) + 1000}.akongo.app`;
    case "knownName": {
      const known = knownValues.get(String(value).trim());
      return known
        ? substituteByKind(known.kind, value, known.extra)
        : pickUniqueLocalized("enclosure", ENCLOSURE_NAMES, value);
    }
    case "campaign":
      return pickLocalized(CAMPAIGN_NAMES, value);
    case "enrichment":
      return fakeEnrichmentName(value);
    case "note":
      return fakeNoteIn(value, currentLang(), false);
    case "answer":
      return fakeNoteIn(value, currentLang(), true);
    case "bullets":
      return fakeBulletText(value);
    case "noteTitle":
      return pickLocalized(NOTE_TITLES, value);
    case "comment":
      return pickLocalized(COMMENT_TEXTS, value);
    case "informationTitle":
      return pickLocalized(INFORMATION_TITLES, value);
    case "taskTitle":
      return fakeTaskTitle(value, extra && extra.category);
    case "taskDescription":
      return pickLocalized(TASK_DESCRIPTIONS, value);
    case "diagnosis":
      return pickLocalized(DIAGNOSIS_TEXTS, value);
    case "instruction":
      return pickLocalized(TREATMENT_INSTRUCTIONS, value);
    case "objective":
      return pickLocalized(GROUP_OBJECTIVES, value);
    case "action":
      return pickLocalized(GROUP_ACTIONS, value);
    case "exercise":
      return pickLocalized(TRAINING_EXERCISES, value);
    case "reinforcer":
      return pickLocalized(REINFORCERS, value);
    case "document":
      return pickLocalized(DOCUMENT_TITLES, value);
    case "questionnaireName":
      return pickLocalized(QUESTIONNAIRE_NAMES, value);
    case "ethogramName":
      return pickLocalized(ETHOGRAM_NAMES, value);
    case "html": {
      // Même graine que la version texte du même contenu (content / contentHtml)
      const plain = plainTextOf(value);
      return `<p>${escapeHtml(fakeNoteIn(plain || value, currentLang(), false))}</p>`;
    }
    default:
      return value;
  }
}

/** Helper pour les cas d'affichage locaux non couverts par le registre (ex: champ générique `user`). */
export function anonymizeFullName(value) {
  if (typeof value !== "string" || !value.trim()) return value;
  return fakeFullName(value);
}

// --- Transformation récursive de documents ----------------------------------

function isPlainObject(node) {
  if (node === null || typeof node !== "object") return false;
  const proto = Object.getPrototypeOf(node);
  // Exclut Timestamp, GeoPoint, DocumentReference, Date… (instances de classes)
  return proto === Object.prototype || proto === null;
}

function substituteField(kind, value, extra) {
  if (typeof value === "string") {
    return value.trim() ? substitute(kind, value, extra) : value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => substituteField(kind, item, extra));
  }
  if (isPlainObject(value) && typeof value.label === "string") {
    // Option react-select {label, value} dont le label porte le nom — le
    // reste de l'objet est AUSSI transformé (il peut imbriquer d'autres
    // champs sensibles, ex. animalEnclosureData.enclosureName)
    return { ...transform(value), label: substitute(kind, value.label, extra) };
  }
  if (TEXT_KINDS.has(kind) && isPlainObject(value)) {
    // Commentaire {text, authorName…} : le texte ; map {blockId: texte} : les valeurs
    const out = transform(value);
    const textProps = TEXT_CONTAINER_PROPS.filter((prop) => typeof value[prop] === "string");
    if (textProps.length) {
      for (const prop of textProps) out[prop] = substituteField(kind, value[prop], extra);
      return out;
    }
    for (const [prop, text] of Object.entries(value)) {
      if (typeof text === "string" && text.trim()) out[prop] = substitute(kind, text, extra);
    }
    return out;
  }
  return transform(value);
}

function substituteText(kind, value, extra) {
  return typeof value === "string" && value.trim() ? substitute(kind, value, extra) : value;
}

// --- Questionnaires de campagne : langue manquante complétée ---------------------
// Contextes et éthogrammes sont multilingues ({fr, en…}) mais souvent saisis
// en français seulement : en démo, la langue de l'interface manquante est
// complétée. Traduction réelle quand le texte est connu (catalogue standard
// des comportements, réponses courantes), sinon contenu fictif cohérent (même
// nombre de réponses, identifiants gardés). Les textes déjà traduits restent.

const QUESTIONNAIRE_LANGS = ["fr", "en", "de"];
const LANG_KEYS = new Set(["fr", "en", "de", "es", "it", "nl"]);

function hasLang(map, lang) {
  return isPlainObject(map) && typeof map[lang] === "string" && map[lang].trim() !== "";
}

function isLangMap(value) {
  return (
    isPlainObject(value) &&
    Object.keys(value).some((key) => LANG_KEYS.has(key) && typeof value[key] === "string")
  );
}

function firstText(map) {
  if (!isPlainObject(map)) return "";
  for (const lang of ["fr", "en", "de", "es", "it", "nl"]) {
    if (hasLang(map, lang)) return map[lang];
  }
  return "";
}

// Complète les langues manquantes ; `overwriteCurrent` remplace aussi la
// langue affichée (contenu fictif : question et réponses doivent aller ensemble).
function withLanguages(map, entry, overwriteCurrent) {
  const out = isPlainObject(map) ? { ...map } : {};
  for (const lang of QUESTIONNAIRE_LANGS) {
    if (!hasLang(map, lang)) out[lang] = entry[lang] || entry.en;
  }
  if (overwriteCurrent) out[currentLang()] = entry[currentLang()] || entry.en;
  return out;
}

function pickUnused(list, seed, used, prefix) {
  const start = mixHash(seed) % list.length;
  for (let step = 0; step < list.length; step++) {
    const index = (start + step) % list.length;
    if (!used.has(`${prefix}:${index}`)) {
      used.add(`${prefix}:${index}`);
      return list[index];
    }
  }
  return list[start];
}

const normalizeAnswer = (text) =>
  stripAccents(String(text || "")).toLowerCase().replace(/[.!?]+$/, "").replace(/\s+/g, " ").trim();

const ANSWER_INDEX = Object.fromEntries(
  Object.entries(ANSWER_GLOSSARY).map(([key, entry]) => [normalizeAnswer(key), entry])
);

function completeTranslationScore(score) {
  const out = isPlainObject(score) ? { ...score } : {};
  for (const lang of QUESTIONNAIRE_LANGS) {
    const current = isPlainObject(out[lang]) ? out[lang] : {};
    const total = Number(current.total ?? current.criticalTotal ?? 0);
    const criticalTotal = Number(current.criticalTotal ?? total);
    out[lang] = { ...current, score: 100, criticalScore: 100, translated: total, criticalTranslated: criticalTotal };
  }
  return out;
}

function translateContextQuestion(item, outItem, used) {
  const lang = currentLang();
  const options = Array.isArray(item.options) ? item.options : [];
  const questionOk = hasLang(item.question, lang);
  if (questionOk && options.every((option) => hasLang(option, lang))) return false;
  const seed = firstText(item.question) || String(item.id || "");
  const outOptions = Array.isArray(outItem.options) ? outItem.options : [];
  const known = options.map((option) => ANSWER_INDEX[normalizeAnswer(firstText(option))]);

  if (options.length && known.every(Boolean)) {
    // Réponses courantes : vraie traduction, question fictive du même type
    outItem.options = options.map((option, index) => ({ ...outOptions[index], ...withLanguages(option, known[index], false) }));
    if (!questionOk) {
      const bank = CONTEXT_QUESTIONS_BY_ANSWER_TYPE[known[0].type] || CONTEXT_QUESTIONS_BY_ANSWER_TYPE.yesno;
      outItem.question = withLanguages(item.question, pickUnused(bank, seed, used, `q-${known[0].type}`), true);
    }
    return true;
  }
  if (!options.length) {
    const type = String(item.type || "");
    const kind = /photo/i.test(type) ? "photo" : /num|count/i.test(type) ? "number" : "text";
    outItem.question = withLanguages(item.question, pickUnused(CONTEXT_OPEN_QUESTIONS[kind], seed, used, `open-${kind}`), true);
    return true;
  }
  // Réponses propres au parc : question et réponses fictives de même forme
  const templates = CONTEXT_TEMPLATES[options.length];
  const template = templates
    ? pickUnused(templates, seed, used, `t${options.length}`)
    : {
        question: { fr: "Quelle situation correspond le mieux ?", en: "Which situation fits best?", de: "Welche Situation trifft am besten zu?" },
        options: options.map((_, index) => ({ fr: `Situation ${index + 1}`, en: `Situation ${index + 1}`, de: `Situation ${index + 1}` })),
      };
  outItem.question = withLanguages(item.question, template.question, true);
  outItem.options = options.map((option, index) => ({ ...outOptions[index], ...withLanguages(option, template.options[index], true) }));
  return true;
}

// Comportements d'un éthogramme (un tableau par catégorie)
function isBehaviorArray(value) {
  return (
    Array.isArray(value) &&
    value.some((item) => isPlainObject(item) && isLangMap(item.optionText) && ("optionProfileId" in item || "categoryId" in item))
  );
}

function translateBehaviorArray(items, outItems) {
  const lang = currentLang();
  const used = new Set(items.map((item) => isPlainObject(item) && item.optionProfileId).filter((id) => STANDARD_BEHAVIORS[id]));
  let customCount = 0;
  items.forEach((item, index) => {
    const target = outItems[index];
    if (!isPlainObject(item) || !isPlainObject(target) || !isLangMap(item.optionText) || hasLang(item.optionText, lang)) return;
    let entry = STANDARD_BEHAVIORS[item.optionProfileId];
    const isStandard = !!entry;
    if (!entry) {
      // Comportement propre au parc : un comportement standard de la même
      // catégorie, pas déjà présent dans l'éthogramme
      const category = item.categoryId || String(item.optionProfileId || "").split(":")[0];
      const candidates = Object.keys(STANDARD_BEHAVIORS).filter((id) => id.startsWith(`${category}:`) && !used.has(id));
      if (candidates.length) {
        const id = candidates[mixHash(firstText(item.optionText) || String(index)) % candidates.length];
        used.add(id);
        entry = STANDARD_BEHAVIORS[id];
      } else {
        customCount += 1;
        entry = { fr: `Comportement ${customCount}`, en: `Behaviour ${customCount}`, de: `Verhalten ${customCount}` };
      }
    }
    target.optionText = withLanguages(item.optionText, entry, !isStandard);
    if (typeof item.label === "string") target.label = entry[lang] || entry.en;
    const help = item.helpData && item.helpData.helpText;
    if (isLangMap(help) && isPlainObject(target.helpData)) {
      const helpEntry = firstText(help)
        ? pick(BEHAVIOR_HELP_TEXTS, firstText(help))
        : { fr: "", en: "", de: "" };
      target.helpData = { ...target.helpData, helpText: withLanguages(help, helpEntry, false) };
    }
  });
}

// --- Textes des documents dont le type est reconnu -----------------------------

// destinationName / name d'une note ou d'un journal : le type de la destination
// décide du texte fictif (titre de tâche, enclos, enrichissement…).
const DESTINATION_KINDS = {
  animal: "animal", enclosure: "enclosure", sector: "sector", group: "group",
  tournee: "tournee", task: "taskTitle", session_note: "enrichment", food: "enrichment",
  odor_refill: "enrichment", substrate_refill: "enrichment", other_refill: "enrichment",
  object: "enrichment", basicEval: "enrichment",
};

function destinationKind(node) {
  return DESTINATION_KINDS[node.destinationType] || DESTINATION_KINDS[node.type] || "animal";
}

function isOfficialContent(node) {
  return node.zooId === "AKONGO_REF" || node.official === true;
}

function replaceTexts(node, out, fields) {
  for (const [field, kind] of Object.entries(fields)) {
    if (typeof node[field] === "string") out[field] = substituteText(kind, node[field]);
  }
}

// Règles appliquées après la passe par clé : `node` porte les valeurs réelles
// (détection, graines), `out` le document déjà transformé.
function applyEntityTextRules(node, out) {
  // Tâche (les notes de tâche portent aussi taskId : exclues)
  if ("taskId" in node && !("noteDataId" in node) && !("eventId" in node) && !("userFeedback" in node)) {
    const isTreatment =
      node.taskType === "administer_treatment" ||
      (node.taskType && node.taskType.value === "administer_treatment") ||
      !!node.linkedPrescriptionId;
    const category = taskCategoryOf(node);
    for (const field of ["title", "taskName"]) {
      if (typeof node[field] !== "string" || !node[field].trim()) continue;
      out[field] = isTreatment ? fakeTreatmentTitle(node[field]) : substitute("taskTitle", node[field], { category });
    }
    if (!isTreatment) replaceTexts(node, out, { description: "taskDescription", taskDescription: "taskDescription" });
    replaceTexts(node, out, { notes: "comment", failureReason: "comment" });
  }

  // Modèle et automatisation de tâches
  if (("automationId" in node && "automationType" in node) || ("templateId" in node && "nextRun" in node)) {
    const category = taskCategoryOf(node);
    if (typeof node.title === "string" && node.title.trim()) out.title = substitute("taskTitle", node.title, { category });
    replaceTexts(node, out, { description: "taskDescription" });
  }

  // Intervention, rappel personnel
  if ("interventionId" in node && "criticality" in node) {
    replaceTexts(node, out, { title: "taskTitle", description: "note" });
  }
  if ("activityId" in node) {
    replaceTexts(node, out, { title: "taskTitle", note: "note" });
  }

  // Groupe FAST
  if (typeof node.name === "string" && ("individualCount" in node || "groupAnimalsId" in node || "useIndividualManagement" in node) && ("userIds" in node || "nextEvaluationDate" in node)) {
    out.name = substituteText("group", node.name);
  }

  // Note, journal, message (la copie de la destination suit son type)
  const isNoteOrLog =
    "noteDataId" in node ||
    ("eventId" in node && !("schemaVersion" in node)) ||
    (node.type === "session_note" && "sessionId" in node);
  if (isNoteOrLog) {
    replaceTexts(node, out, { content: "note", text: "note", description: "note", notes: "note" });
    if (typeof node.name === "string") out.name = substituteText(destinationKind(node), node.name);
  }

  // Traduction automatique d'une note : même texte fictif, dans chaque langue
  if (typeof node.userFeedback === "string" && isPlainObject(node.userFeedbackTranslation)) {
    out.userFeedbackTranslation = {};
    for (const [lang, text] of Object.entries(node.userFeedbackTranslation)) {
      out.userFeedbackTranslation[lang] = typeof text === "string" ? fakeNoteIn(node.userFeedback, lang, false) : text;
    }
  }

  // Notification de note (les notes-questions, sans noteId, sont de la configuration)
  if ("noteId" in node && "noteAuthorName" in node) {
    replaceTexts(node, out, { noteContent: "note" });
  }

  // Note de composition d'un enclos ou d'un groupe
  if ("changeType" in node && "versionNumber" in node) {
    replaceTexts(node, out, { note: "note" });
  }

  // Note d'évaluation, note privée AKONGO d'un groupe
  if ("linkedEvaluationId" in node || ("authorId" in node && "groupId" in node && "content" in node && "title" in node && !("noteDataId" in node))) {
    replaceTexts(node, out, { title: "noteTitle", content: "note" });
  }

  // Information (annonce)
  if ("informationId" in node) {
    replaceTexts(node, out, { title: "informationTitle", name: "informationTitle", content: "note", description: "note", contentHtml: "html" });
  }

  // Enrichissement du catalogue du parc, ou choisi dans une évaluation
  if ("enrichId" in node && typeof node.name === "string" && !isOfficialContent(node)) {
    out.name = substituteText("enrichment", node.name);
    replaceTexts(node, out, { originalName: "enrichment" });
    if (isPlainObject(node.trad)) {
      out.trad = {};
      for (const lang of Object.keys(node.trad)) out.trad[lang] = pickLocalizedIn(ENRICHMENT_NAMES, node.name, lang);
    }
    if (Array.isArray(node.sectors)) out.sectors = node.sectors.map((name) => substituteText("sector", name));
  }

  // Enrichissement installé (session), hors séance de training
  if ("sessionId" in node && ("selectedEnrich" in node || "dateInstall" in node) && !("formVersion" in node)) {
    replaceTexts(node, out, { name: "enrichment", description: "note", note: "note" });
  }

  // Évaluation d'enrichissement
  if ("evaluationId" in node && "enrichQuestionsList" in node) {
    replaceTexts(node, out, { name: "enrichment" });
    if (Array.isArray(node.selectedEnrichData) && Array.isArray(out.selectedEnrichData)) {
      node.selectedEnrichData.forEach((item, index) => {
        if (isPlainObject(item) && typeof item.name === "string" && isPlainObject(out.selectedEnrichData[index])) {
          out.selectedEnrichData[index].name = substituteText("enrichment", item.name);
        }
      });
    }
  }

  // Évaluation rapide : copies des noms de l'individu ou du groupe évalué
  if ("answers" in node && ("evaluationId" in node || "gridId" in node || "indiceScore" in node)) {
    if (isPlainObject(node.animalData) && typeof node.animalData.name === "string") out.animalData.name = substituteText("animal", node.animalData.name);
    if (isPlainObject(node.groupData) && typeof node.groupData.name === "string") out.groupData.name = substituteText("group", node.groupData.name);
    replaceTexts(node, out, { notes: "note" });
    if (isPlainObject(node.answers) && isPlainObject(out.answers)) {
      for (const [questionId, answer] of Object.entries(node.answers)) {
        if (isPlainObject(answer) && isPlainObject(out.answers[questionId])) {
          replaceTexts(answer, out.answers[questionId], { note: "comment" });
        }
      }
    }
  }

  // Rapport quotidien : entrées libres et synthèse
  if ("reportId" in node && ("humanEntries" in node || "humanSummary" in node || "dailyStats" in node || "aiReport" in node)) {
    if (isPlainObject(node.aiReport) && isPlainObject(out.aiReport)) replaceTexts(node.aiReport, out.aiReport, { text: "answer" });
    if (Array.isArray(node.aiReportHistory) && Array.isArray(out.aiReportHistory)) {
      node.aiReportHistory.forEach((entry, index) => {
        if (isPlainObject(entry) && isPlainObject(out.aiReportHistory[index])) replaceTexts(entry, out.aiReportHistory[index], { text: "answer" });
      });
    }
    if (Array.isArray(node.humanEntries) && Array.isArray(out.humanEntries)) {
      node.humanEntries.forEach((entry, index) => {
        if (isPlainObject(entry) && isPlainObject(out.humanEntries[index])) {
          replaceTexts(entry, out.humanEntries[index], { content: "note", contentHTML: "html", contentHtml: "html" });
        }
      });
    }
    if (isPlainObject(node.humanSummary) && isPlainObject(out.humanSummary)) {
      replaceTexts(node.humanSummary, out.humanSummary, { content: "note", contentHtml: "html", contentHTML: "html", manualContent: "note" });
    }
  }

  // Contribution libre
  if ("reportId" in node && "authorRole" in node && node.type === "freeform") {
    replaceTexts(node, out, { content: "note", contentHTML: "html", contentHtml: "html" });
  }

  // Contribution structurée : réponses texte (les questions du modèle restent)
  if ("reportId" in node && Array.isArray(node.responses) && Array.isArray(out.responses)) {
    node.responses.forEach((response, index) => {
      const target = out.responses[index];
      if (!isPlainObject(response) || !isPlainObject(target) || response.responseType !== "text") return;
      if (typeof response.value === "string") {
        target.value = substituteText("answer", response.value);
      } else if (Array.isArray(response.value) && Array.isArray(target.value)) {
        response.value.forEach((entry, entryIndex) => {
          if (isPlainObject(entry) && typeof entry.value === "string" && isPlainObject(target.value[entryIndex])) {
            target.value[entryIndex].value = substituteText("answer", entry.value);
          }
        });
      } else if (isPlainObject(response.value)) {
        for (const [entityId, entry] of Object.entries(response.value)) {
          if (isPlainObject(entry) && typeof entry.value === "string" && isPlainObject(target.value[entityId])) {
            target.value[entityId].value = substituteText("answer", entry.value);
          } else if (typeof entry === "string" && entry.trim()) {
            target.value[entityId] = substituteText("answer", entry);
          }
        }
      }
    });
  }

  // Événement du rapport collaboratif
  // (valeurs de choix gardées : seules les réponses des blocs texte changent)
  if ("schemaVersion" in node && "payload" in node && isPlainObject(node.payload) && isPlainObject(out.payload)) {
    const payload = node.payload;
    const blockType = isPlainObject(payload.snapshot) ? payload.snapshot.responseType : undefined;
    const looksLikeText = (value) => typeof value === "string" && /\s/.test(value.trim());
    if (node.type === "answer_set" && (blockType === "text" || (!blockType && looksLikeText(payload.value)))) {
      replaceTexts(payload, out.payload, { value: "answer" });
    }
    if (node.type === "cell_resolved" && looksLikeText(payload.chosenValue)) {
      replaceTexts(payload, out.payload, { chosenValue: "answer" });
    }
    const payloadKinds = {
      cell_comment: { text: "comment" },
      cell_resolved: { note: "comment" },
      report_note: { text: "note" },
    }[node.type];
    if (payloadKinds) replaceTexts(payload, out.payload, payloadKinds);
  }

  // Rapport Rafiki vétérinaire (puces de priorité gardées)
  if (isPlainObject(node.highlights) && isPlainObject(node.sectors)) {
    const bullets = (source, target) => {
      if (!isPlainObject(source) || !isPlainObject(target)) return;
      replaceTexts(source, target, { text: "bullets" });
      if (isPlainObject(source.previous) && isPlainObject(target.previous)) replaceTexts(source.previous, target.previous, { text: "bullets" });
    };
    bullets(node.highlights, out.highlights);
    for (const sectorId of Object.keys(node.sectors)) bullets(node.sectors[sectorId], out.sectors && out.sectors[sectorId]);
  }

  // Rapport de visite
  if ("visitDate" in node && "contentHtml" in node) {
    replaceTexts(node, out, { title: "noteTitle", contentHtml: "html", rafikiRationale: "note" });
  }

  // Relevé santé ou environnement : notes libres
  if ("collectionId" in node && "parameterKey" in node) {
    replaceTexts(node, out, { notes: "note" });
  }

  // Ordonnance (médicaments gardés)
  if (Array.isArray(node.treatments) && ("prescriptionId" in node || "diagnosis" in node || "veterinaryInfo" in node || "startDate" in node)) {
    replaceTexts(node, out, { notes: "note", additionalInfo: "note", instructions: "instruction", cancelNotes: "comment", teamName: "clinic" });
    node.treatments.forEach((treatment, index) => {
      if (isPlainObject(treatment) && isPlainObject(out.treatments[index])) {
        replaceTexts(treatment, out.treatments[index], { instructions: "instruction", injectionSiteOther: "comment" });
      }
    });
    if (Array.isArray(node.administrations) && Array.isArray(out.administrations)) {
      node.administrations.forEach((administration, index) => {
        if (isPlainObject(administration) && isPlainObject(out.administrations[index])) {
          replaceTexts(administration, out.administrations[index], { notes: "comment", failureReasonOther: "comment" });
        }
      });
    }
  }

  // Objectif, action d'un groupe
  const isObjective = "progress" in node && "title" in node && ("dueDate" in node || "groupId" in node);
  const isAction = !isObjective && "title" in node && "priority" in node && ("assignedTo" in node || "objectiveId" in node) && !("taskId" in node);
  if (isObjective || isAction) {
    replaceTexts(node, out, { title: isObjective ? "objective" : "action", description: "note" });
    if (isPlainObject(node.proposalSource) && isPlainObject(out.proposalSource)) {
      replaceTexts(node.proposalSource, out.proposalSource, { rationale: "note" });
    }
  }

  // Séance de training, exercice
  if ("sessionId" in node && "formVersion" in node && isPlainObject(node.behavior) && isPlainObject(out.behavior)) {
    replaceTexts(node.behavior, out.behavior, { otherText: "comment" });
  }
  // Séance de training (fiche v12) : observateurs et entraîneur secondaire
  // choisis dans l'équipe ({ name, userId }), objectifs et contexte saisis
  // librement — la clé « name » seule est trop générique pour la passe par clé.
  if ("sessionId" in node && ("observers" in node || "secondaryOperator" in node || "sessionObjectives" in node || "environmentContext" in node)) {
    if (Array.isArray(node.observers) && Array.isArray(out.observers)) {
      node.observers.forEach((person, index) => {
        if (isPlainObject(person) && typeof person.name === "string" && isPlainObject(out.observers[index])) {
          out.observers[index] = { ...out.observers[index], name: anonymizeFullName(person.name) };
        } else if (typeof person === "string") {
          out.observers[index] = anonymizeFullName(person);
        }
      });
    }
    if (isPlainObject(node.secondaryOperator) && typeof node.secondaryOperator.name === "string" && isPlainObject(out.secondaryOperator)) {
      out.secondaryOperator = { ...out.secondaryOperator, name: anonymizeFullName(node.secondaryOperator.name) };
    }
    if (Array.isArray(node.sessionObjectives) && Array.isArray(out.sessionObjectives)) {
      node.sessionObjectives.forEach((objective, index) => {
        if (isPlainObject(objective) && !objective.key && isPlainObject(out.sessionObjectives[index])) {
          replaceTexts(objective, out.sessionObjectives[index], { label: "comment" });
        }
      });
    }
    if (isPlainObject(node.environmentContext) && isPlainObject(out.environmentContext)) {
      replaceTexts(node.environmentContext, out.environmentContext, { otherText: "comment" });
    }
  }
  // Catalogue d'exercices de training : configuration du parc, gardé tel quel
  // (son empreinte de validation porte sur ces textes).

  // Préférence alimentaire d'un animal (zoo/{zooId}/animalPreferences) : la
  // note libre et, dans l'historique, la note et l'auteur de chaque changement
  // (« byName », trop générique pour la passe par clé). Le nom de l'aliment
  // reste réel. Même règle que le mobile (app/utils/anonymization/engine.js).
  if ("preferenceId" in node && "kind" in node && isPlainObject(node.food)) {
    replaceTexts(node, out, { note: "note" });
    if (Array.isArray(node.history) && Array.isArray(out.history)) {
      node.history.forEach((entry, index) => {
        if (isPlainObject(entry) && isPlainObject(out.history[index])) {
          replaceTexts(entry, out.history[index], { note: "note", byName: "fullName" });
        }
      });
    }
  }

  // Questionnaire de contexte d'un parc (les contextes publics AKONGO restent)
  if (Array.isArray(node.serie) && node.isPublic !== true && Array.isArray(out.serie)) {
    const used = new Set();
    let completed = false;
    node.serie.forEach((item, index) => {
      if (isPlainObject(item) && isLangMap(item.question) && isPlainObject(out.serie[index])) {
        if (translateContextQuestion(item, out.serie[index], used)) completed = true;
      }
    });
    if (completed && "translationScore" in node) out.translationScore = completeTranslationScore(node.translationScore);
    if (Array.isArray(node.zooAvailability) && Array.isArray(out.zooAvailability)) {
      out.zooAvailability = out.zooAvailability.map((zoo, index) => {
        const real = node.zooAvailability[index];
        return isPlainObject(zoo) && isPlainObject(real) && typeof real.label === "string"
          ? { ...zoo, label: substitute("zoo", real.label) }
          : zoo;
      });
    }
  }

  // Nom d'un contexte (document ou référence dans une campagne)
  if ("contextId" in node && !node.isNoContextOption) {
    const realName = [node.contextName, node.contextNameFr, node.label].find((name) => typeof name === "string" && name.trim());
    if (realName) {
      const fake = substitute("questionnaireName", realName);
      if (typeof node.contextName === "string") out.contextName = fake;
      if (typeof node.label === "string") out.label = fake;
      if (typeof node.searchableText === "string") out.searchableText = fake;
      if (typeof node.contextNameFr === "string") out.contextNameFr = pickLocalizedIn(QUESTIONNAIRE_NAMES, realName, "fr");
      if (typeof node.contextNameEn === "string") out.contextNameEn = pickLocalizedIn(QUESTIONNAIRE_NAMES, realName, "en");
      if (typeof node.contextInternationalName === "string") out.contextInternationalName = pickLocalizedIn(QUESTIONNAIRE_NAMES, realName, "en");
    }
  }

  // Éthogramme : comportements d'un parc (catalogue standard traduit), score
  // de traduction complété (les éthogrammes officiels restent)
  for (const [key, value] of Object.entries(node)) {
    if (isBehaviorArray(value) && Array.isArray(out[key])) translateBehaviorArray(value, out[key]);
  }
  const currentScore = isPlainObject(node.translationScore) ? node.translationScore[currentLang()] : null;
  if ("obsId" in node && Array.isArray(node.categories) && node.isOfficial !== true && !(currentScore && Number(currentScore.criticalScore) >= 100)) {
    out.translationScore = completeTranslationScore(node.translationScore);
  }
  if (node.isOfficial === true && typeof node.obsName === "string") out.obsName = node.obsName;

  // Document du parc
  if ("documentId" in node && "documentType" in node) {
    replaceTexts(node, out, { title: "document", description: "note" });
    if (isPlainObject(node.file) && typeof node.file.fileName === "string" && isPlainObject(out.file)) {
      const extension = (/\.[a-z0-9]{1,5}$/i.exec(node.file.fileName) || [""])[0];
      const base = stripAccents(substituteText("document", node.title || node.file.fileName))
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      out.file.fileName = `${base || "document"}${extension}`;
    }
  }
}

function transform(node) {
  if (Array.isArray(node)) return node.map(transform);
  if (!isPlainObject(node)) return node;

  const out = {};
  let hadEmailField = null;
  const photosActive = isDemoPhotosActive();
  // Contexte du document (valeurs RÉELLES, avant substitution) : seed
  // d'identité pour l'injection, indices d'espèce pour un picking cohérent
  // (clic sur une girafe → photo de girafe)
  let nodeCtx = null;
  if (photosActive) {
    const seed =
      ["animalId", "animalName", "enclosureId", "enclosureName", "sectorId",
       "sectorName", "groupId", "groupName", "id"]
        .map((k) => node[k])
        .find((v) => typeof v === "string" && v.trim()) || null;
    const species = ["specieName", "specieLatinName", "specieEnglishName", "customSpecie"]
      .map((k) => node[k])
      .filter((v) => typeof v === "string" && v.trim());
    nodeCtx = { seed, species };
  }
  for (const [key, value] of Object.entries(node)) {
    if (photosActive && PHOTO_FIELDS.has(key)) {
      out[key] = substitutePhoto(value, key, nodeCtx);
      continue;
    }
    const kind = key === "destinationName" ? destinationKind(node) : FIELD_KINDS[key];
    if (kind) {
      out[key] = substituteField(kind, value);
      if (kind === "email" && typeof value === "string") hadEmailField = key;
    } else if (OPTION_LABEL_KINDS[key] && isPlainObject(value) && typeof value.label === "string") {
      out[key] = { ...transform(value), label: substitute(OPTION_LABEL_KINDS[key], value.label) };
    } else {
      out[key] = transform(value);
    }
  }

  // Injection quand le champ de photo de profil est totalement ABSENT du
  // document (les fiches créées sans photo n'ont souvent pas le champ du
  // tout, la boucle ci-dessus ne le visite donc jamais). Détection du type
  // d'entité par précédence ; sur un doc référençant (note, tâche…) le champ
  // injecté est simplement inutilisé.
  if (photosActive && photoResolver && nodeCtx && nodeCtx.seed) {
    const profileField =
      "animalName" in node || "animalId" in node
        ? "animalProfilePicture"
        : "enclosureName" in node || "enclosureId" in node
        ? "enclosureProfilePicture"
        : "sectorName" in node || "sectorId" in node
        ? "sectorProfilePicture"
        : "groupName" in node || "groupId" in node
        ? "groupPhoto"
        : null;
    if (profileField && !(profileField in node)) {
      out[profileField] = substitutePhoto(null, profileField, nodeCtx);
    }
  }

  // Cohérence email ↔ identité : si l'objet porte prénom/nom anonymisés,
  // reconstruit l'email à partir d'eux plutôt que du hash de l'email réel.
  if (hadEmailField) {
    const first =
      out.userFirstname || out.firstName || out.firstname ||
      (typeof out.userName === "string" ? out.userName.split(" ")[0] : null) ||
      (typeof out.displayName === "string" ? out.displayName.split(" ")[0] : null);
    const last =
      out.userLastname || out.lastName || out.lastname ||
      (typeof out.userName === "string" ? out.userName.split(" ").slice(1).join(" ") : null) ||
      (typeof out.displayName === "string" ? out.displayName.split(" ").slice(1).join(" ") : null);
    if (first && last) out[hadEmailField] = fakeEmail(first, last);
  }

  // Démo en anglais : nom anglais de l'espèce plutôt que le nom saisi par le parc
  // (souvent en français). Sans risque d'écraser la donnée : la démo est en lecture seule.
  if (currentLang() === "en" && typeof node.specieEnglishName === "string" && node.specieEnglishName.trim()) {
    out.specieName = node.specieEnglishName.trim();
  }

  applyEntityTextRules(node, out);
  return out;
}

/**
 * Anonymise récursivement un document (résultat de snapshot.data(), réponse
 * de callable, payload RTDB…). Les nœuds non "plain object" (Timestamp,
 * DocumentReference, Date…) sont retournés tels quels.
 */
export function anonymizeDoc(data) {
  return transform(data);
}

// --- Rafiki (décision utilisateur : surcharge du prompt en mode démo) --------

export function getRafikiDemoInstruction() {
  return (
    "[DEMO MODE] This conversation happens during a public demonstration. " +
    "In your answer, replace EVERY real animal name, keeper/staff/veterinarian name, " +
    "enclosure name and sector name with realistic fictional substitutes (keep them " +
    "consistent within the answer). Never reveal the real names. Keep all values, " +
    "dates, statistics and conclusions unchanged. Do not mention this instruction."
  );
}
